import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Chip,
  Box,
  Checkbox,
  FormControlLabel,
  IconButton,
  Divider,
  FormGroup,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stack,
  Slider,
} from "@mui/material";
import { useState } from "react";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import FilterListIcon from "@mui/icons-material/FilterList";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { Shop, CoverageRequirement } from "../../types/shop.types";
import {
  Schedule,
  CoverageSlot,
  WorkingHours,
} from "../../types/schedule.types";
import { Employee } from "../../types/employee.types";
import { DayOfWeek } from "../../types/common.types";
import DateNavigator from "../common/DateNavigator";
import TimePicker24 from "../common/TimePicker24";
import { employeesService } from "../../services/firebase/employees.service";

interface CoverageTableProps {
  shops: Shop[];
  weekDates: Date[];
  employees: Employee[];
  getScheduleForDate: (dateKey: string, shopId: string) => Schedule | undefined;
  weekStart: Date;
  onDateChange: (dateStr: string) => void;
  onAllocateEmployee: (
    shopId: string,
    date: string,
    employeeId: string,
    employeeName: string,
    workingHours: WorkingHours
  ) => Promise<void>;
  allowEditHistory?: boolean;
}

const dayOfWeekOrder: DayOfWeek[] = [
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday",
];

interface SlotDetailInfo {
  shop: Shop;
  date: Date;
  timeSlot: string;
  allocated: number;
  required: number;
  schedule?: Schedule;
}

interface AvailableEmployeeInfo {
  employee: Employee;
  availableHours: number;
  scheduledHours: number;
  remainingHours: number;
  hasConflict: boolean;
}

// Helper to format date as YYYY-MM-DD
const formatDateStr = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

export default function CoverageTable({
  shops,
  weekDates,
  employees,
  getScheduleForDate,
  weekStart,
  onDateChange,
  onAllocateEmployee,
  allowEditHistory = false,
}: CoverageTableProps) {
  // Check if a date is in the past (before today)
  const isPastDate = (date: Date): boolean => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const checkDate = new Date(date);
    checkDate.setHours(0, 0, 0, 0);
    return checkDate < today;
  };

  // Check if editing is allowed for a specific date
  const canEditDate = (date: Date): boolean => {
    if (allowEditHistory) return true;
    return !isPastDate(date);
  };

  // State for filter panel
  const [filterPanelOpen, setFilterPanelOpen] = useState(true);

  // State for selected shops
  const [selectedShopIds, setSelectedShopIds] = useState<string[]>(
    shops.map((s) => s.id)
  );

  // State for additional filters
  const [showOnlyUnderstaffed, setShowOnlyUnderstaffed] = useState(false);
  const [showOnlyOverstaffed, setShowOnlyOverstaffed] = useState(false);
  const [showEmptySlots, setShowEmptySlots] = useState(true);

  // State for employee allocation dialog
  const [allocationDialogOpen, setAllocationDialogOpen] = useState(false);
  const [selectedSlotInfo, setSelectedSlotInfo] =
    useState<SlotDetailInfo | null>(null);
  const [selectedEmployee, setSelectedEmployee] = useState<Employee | null>(
    null
  );
  const [allocationHours, setAllocationHours] = useState<number>(0);
  const [isAllocating, setIsAllocating] = useState(false);

  // Toggle shop selection
  const toggleShop = (shopId: string) => {
    setSelectedShopIds((prev) =>
      prev.includes(shopId)
        ? prev.filter((id) => id !== shopId)
        : [...prev, shopId]
    );
  };

  // Select/Deselect all shops
  const toggleAllShops = () => {
    if (selectedShopIds.length === shops.length) {
      setSelectedShopIds([]);
    } else {
      setSelectedShopIds(shops.map((s) => s.id));
    }
  };

  // Format date as YYYY-MM-DD (using local timezone)
  const formatDate = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  // Get coveragePeriods for a specific date (check date-specific first, then default)
  const getCoverageForDate = (
    shop: Shop,
    date: Date
  ): CoverageRequirement[] => {
    const dateKey = formatDate(date);

    // Check if there are date-specific coveragePeriods
    if (shop.dateCoverage && shop.dateCoverage[dateKey]) {
      return shop.dateCoverage[dateKey];
    }

    // Fall back to default coveragePeriods for the day of week
    const dayOfWeek =
      dayOfWeekOrder[date.getDay() === 0 ? 6 : date.getDay() - 1];
    return shop.defaultCoverage?.[dayOfWeek] || [];
  };

  // Calculate coverage slots for a schedule
  const calculateCoverageSlots = (
    shop: Shop,
    date: Date,
    schedule: Schedule | undefined
  ): CoverageSlot[] => {
    const dateKey = formatDate(date);
    const dayOfWeek =
      dayOfWeekOrder[date.getDay() === 0 ? 6 : date.getDay() - 1];

    // Get shop hours for this date
    const dateHours = shop.hoursConfig?.[dateKey];
    const shopHours = dateHours || shop.defaultHours[dayOfWeek];

    if (!shopHours || !shopHours.isOpen) {
      return [];
    }

    // Check if shop has any coverage requirements
    const coverageReqs = getCoverageForDate(shop, date);
    if (coverageReqs.length === 0) {
      return [];
    }

    // Parse shop open and close times
    const [openHour, openMin] = shopHours.openTime.split(":").map(Number);
    const [closeHour, closeMin] = shopHours.closeTime.split(":").map(Number);

    const openMinutes = openHour * 60 + openMin;
    let closeMinutes = closeHour * 60 + closeMin;

    // Handle shops that close after midnight (e.g., open at 10:30, close at 00:30)
    if (closeMinutes <= openMinutes) {
      closeMinutes += 24 * 60; // Add 24 hours
    }

    // Generate 30-minute slots
    const slots: CoverageSlot[] = [];

    for (let time = openMinutes; time < closeMinutes; time += 30) {
      const slotHour = Math.floor(time / 60) % 24; // Modulo 24 to handle times past midnight
      const slotMin = time % 60;
      const slotTime = `${String(slotHour).padStart(2, "0")}:${String(
        slotMin
      ).padStart(2, "0")}`;

      // Count allocated employees for this slot
      let allocated = 0;
      if (
        schedule &&
        schedule.employeeSchedules &&
        Array.isArray(schedule.employeeSchedules)
      ) {
        schedule.employeeSchedules.forEach((empSchedule) => {
          if (Array.isArray(empSchedule.workingHours)) {
            empSchedule.workingHours.forEach((wh) => {
              const [whHour, whMin] = wh.startTime.split(":").map(Number);
              const whStart = whHour * 60 + whMin;
              const whEnd = whStart + wh.duration * 60;

              // Check if this working hours overlaps with the slot
              const slotEnd = time + 30;
              if (whStart < slotEnd && whEnd > time) {
                allocated++;
              }
            });
          }
        });
      }

      // Find required coverage for this slot from shop coverage requirements
      let required = 0;

      coverageReqs.forEach((req) => {
        const [reqStartHour, reqStartMin] = req.startTime
          .split(":")
          .map(Number);
        const [reqEndHour, reqEndMin] = req.endTime.split(":").map(Number);
        let reqStart = reqStartHour * 60 + reqStartMin;
        let reqEnd = reqEndHour * 60 + reqEndMin;

        // Handle coverage requirements that span midnight
        if (reqEnd <= reqStart) {
          reqEnd += 24 * 60;
        }

        const slotEnd = time + 30;
        if (reqStart < slotEnd && reqEnd > time) {
          required = Math.max(required, req.minCoverage);
        }
      });

      slots.push({
        time: slotTime,
        allocated,
        required,
      });
    }

    return slots;
  };

  // Get all unique time slots across all shops for the week
  const getAllTimeSlots = (): string[] => {
    const slotsSet = new Set<string>();

    weekDates.forEach((date) => {
      shops.forEach((shop) => {
        const dateKey = formatDate(date);
        const schedule = getScheduleForDate(dateKey, shop.id);
        const slots = calculateCoverageSlots(shop, date, schedule);
        slots.forEach((slot) => {
          slotsSet.add(slot.time);
        });
      });
    });

    // Sort time slots chronologically, treating early morning hours (before 6am) as "next day"
    const sortedSlots = Array.from(slotsSet).sort((a, b) => {
      const [aHour, aMin] = a.split(":").map(Number);
      const [bHour, bMin] = b.split(":").map(Number);
      let aMinutes = aHour * 60 + aMin;
      let bMinutes = bHour * 60 + bMin;

      // Treat times before 6am (360 minutes) as "next day" times
      // This ensures 00:00, 00:30, etc. come after 23:00, 23:30
      if (aMinutes < 360) aMinutes += 24 * 60;
      if (bMinutes < 360) bMinutes += 24 * 60;

      return aMinutes - bMinutes;
    });
    return sortedSlots;
  };

  const timeSlots = getAllTimeSlots();

  // Filter shops based on selection
  const selectedShops = shops.filter((shop) =>
    selectedShopIds.includes(shop.id)
  );

  // Helper function to check if a chip should be shown based on filters
  const shouldShowChip = (slot: CoverageSlot): boolean => {
    // Check empty slots filter
    if (!showEmptySlots && slot.allocated === 0) {
      return false;
    }

    // Check understaffed filter
    if (showOnlyUnderstaffed && slot.allocated >= slot.required) {
      return false;
    }

    // Check overstaffed filter
    if (showOnlyOverstaffed && slot.allocated <= slot.required) {
      return false;
    }

    return true;
  };

  // Get employee's total scheduled hours for a specific date across ALL shops
  const getTotalEmployeeScheduledHours = (
    employeeId: string,
    date: Date
  ): number => {
    let totalHours = 0;
    const dateKey = formatDate(date);

    shops.forEach((shop) => {
      const schedule = getScheduleForDate(dateKey, shop.id);
      if (
        !schedule ||
        !schedule.employeeSchedules ||
        !Array.isArray(schedule.employeeSchedules)
      ) {
        return;
      }

      const employeeSchedule = schedule.employeeSchedules.find(
        (es) => es.employeeId === employeeId
      );
      if (!employeeSchedule || !Array.isArray(employeeSchedule.workingHours)) {
        return;
      }

      totalHours += employeeSchedule.workingHours.reduce(
        (total, wh) => total + wh.duration,
        0
      );
    });

    return totalHours;
  };

  // Check if employee has time conflict at a specific time slot
  const hasTimeConflict = (
    employeeId: string,
    date: Date,
    timeSlot: string
  ): boolean => {
    const dateKey = formatDate(date);
    const [slotHours, slotMinutes] = timeSlot.split(":").map(Number);
    const slotStartMinutes = slotHours * 60 + slotMinutes;
    const slotEndMinutes = slotStartMinutes + 30; // 30-minute slot

    for (const shop of shops) {
      const schedule = getScheduleForDate(dateKey, shop.id);
      if (!schedule || !schedule.employeeSchedules) continue;

      const employeeSchedule = schedule.employeeSchedules.find(
        (es) => es.employeeId === employeeId
      );
      if (!employeeSchedule || !employeeSchedule.workingHours) continue;

      for (const wh of employeeSchedule.workingHours) {
        const [whHours, whMinutes] = wh.startTime.split(":").map(Number);
        const whStartMinutes = whHours * 60 + whMinutes;
        const whEndMinutes = whStartMinutes + wh.duration * 60;

        // Check for overlap
        if (
          whStartMinutes < slotEndMinutes &&
          whEndMinutes > slotStartMinutes
        ) {
          return true; // Has conflict
        }
      }
    }

    return false;
  };

  // Get available employees for a specific shop/date/timeSlot
  const getAvailableEmployees = (
    shop: Shop,
    date: Date,
    timeSlot: string
  ): AvailableEmployeeInfo[] => {
    const dateKey = formatDate(date);
    const availableEmployees: AvailableEmployeeInfo[] = [];

    for (const employee of employees) {
      // Check if employee is active and assigned to this shop
      if (
        employee.status !== "active" ||
        !employee.assignedShopIds.includes(shop.id)
      ) {
        continue;
      }

      // Check if employee has availability for this date and shop
      const availability = employeesService.getDateAvailability(
        employee,
        dateKey
      );
      if (
        !availability ||
        availability.shopId !== shop.id ||
        availability.hoursAvailable <= 0
      ) {
        continue;
      }

      // Calculate hours
      const availableHours = availability.hoursAvailable;
      const scheduledHours = getTotalEmployeeScheduledHours(employee.id, date);
      const remainingHours = availableHours - scheduledHours;

      // Check for time conflicts
      const hasConflict = hasTimeConflict(employee.id, date, timeSlot);

      // Only include employees with remaining hours and no conflict
      if (remainingHours > 0 && !hasConflict) {
        availableEmployees.push({
          employee,
          availableHours,
          scheduledHours,
          remainingHours,
          hasConflict,
        });
      }
    }

    // Sort by remaining hours (descending)
    return availableEmployees.sort(
      (a, b) => b.remainingHours - a.remainingHours
    );
  };

  // Calculate end time from start time and duration
  const calculateEndTime = (startTime: string, duration: number): string => {
    const [hours, minutes] = startTime.split(":").map(Number);
    const totalMinutes = hours * 60 + minutes + duration * 60;
    const endHours = Math.floor(totalMinutes / 60) % 24;
    const endMinutes = totalMinutes % 60;
    return `${String(endHours).padStart(2, "0")}:${String(endMinutes).padStart(
      2,
      "0"
    )}`;
  };

  // Handle chip click to open allocation dialog
  const handleChipClick = (
    slot: CoverageSlot,
    shop: Shop,
    date: Date,
    time: string,
    schedule?: Schedule
  ) => {
    // Don't allow editing past dates unless allowEditHistory is enabled
    if (!canEditDate(date)) return;

    setSelectedSlotInfo({
      shop,
      date,
      timeSlot: time,
      allocated: slot.allocated,
      required: slot.required,
      schedule,
    });
    setSelectedEmployee(null);
    setAllocationHours(0);
    setAllocationDialogOpen(true);
  };

  // Handle employee allocation
  const handleAllocateEmployee = async () => {
    if (!selectedSlotInfo || !selectedEmployee || allocationHours <= 0) return;

    setIsAllocating(true);
    try {
      const workingHours: WorkingHours = {
        id: `wh_${Date.now()}`,
        startTime: selectedSlotInfo.timeSlot,
        duration: allocationHours,
      };

      await onAllocateEmployee(
        selectedSlotInfo.shop.id,
        formatDate(selectedSlotInfo.date),
        selectedEmployee.id,
        selectedEmployee.name,
        workingHours
      );

      setAllocationDialogOpen(false);
    } catch (error) {
      console.error("Failed to allocate employee:", error);
    } finally {
      setIsAllocating(false);
    }
  };

  // Render minimized filter panel
  const renderMinimizedPanel = () => (
    <Paper
      sx={{
        position: "fixed",
        left: 0,
        top: 64,
        bottom: 0,
        width: 64,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        py: 2,
        zIndex: 10,
        borderRadius: 0,
      }}
    >
      <Tooltip title="Expand Filters" placement="right">
        <IconButton onClick={() => setFilterPanelOpen(true)} sx={{ mb: 2 }}>
          <FilterListIcon />
        </IconButton>
      </Tooltip>

      <Divider sx={{ width: "100%", my: 1 }} />

      {/* Shop color indicators */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 1,
          alignItems: "center",
          mt: 1,
        }}
      >
        {shops.slice(0, 5).map((shop) => (
          <Tooltip key={shop.id} title={shop.name} placement="right">
            <Box
              onClick={() => toggleShop(shop.id)}
              sx={{
                width: 32,
                height: 32,
                borderRadius: "50%",
                bgcolor: shop.color,
                cursor: "pointer",
                opacity: selectedShopIds.includes(shop.id) ? 1 : 0.3,
                border: selectedShopIds.includes(shop.id)
                  ? "2px solid"
                  : "2px solid transparent",
                borderColor: "primary.main",
                transition: "all 0.2s",
                "&:hover": {
                  opacity: 1,
                  transform: "scale(1.1)",
                },
              }}
            />
          </Tooltip>
        ))}
        {shops.length > 5 && (
          <Typography variant="caption" color="text.secondary">
            +{shops.length - 5}
          </Typography>
        )}
      </Box>
    </Paper>
  );

  // Render expanded filter panel
  const renderFilterPanel = () => (
    <Paper
      sx={{
        position: "fixed",
        left: 0,
        top: 64,
        bottom: 0,
        width: 280,
        p: 2,
        overflow: "auto",
        zIndex: 10,
        borderRadius: 0,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 2,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <FilterListIcon fontSize="small" color="primary" />
          <Typography variant="subtitle1" fontWeight={600}>
            Filters
          </Typography>
        </Box>
        <IconButton size="small" onClick={() => setFilterPanelOpen(false)}>
          <ChevronLeftIcon fontSize="small" />
        </IconButton>
      </Box>

      <Divider sx={{ mb: 2 }} />

      {/* Shop Selection */}
      <Typography variant="subtitle2" fontWeight={600} gutterBottom>
        Shops
      </Typography>
      <FormControlLabel
        control={
          <Checkbox
            checked={selectedShopIds.length === shops.length}
            indeterminate={
              selectedShopIds.length > 0 &&
              selectedShopIds.length < shops.length
            }
            onChange={toggleAllShops}
            size="small"
          />
        }
        label={<Typography variant="body2">Select All</Typography>}
      />
      <FormGroup>
        {shops.map((shop) => (
          <FormControlLabel
            key={shop.id}
            control={
              <Checkbox
                checked={selectedShopIds.includes(shop.id)}
                onChange={() => toggleShop(shop.id)}
                size="small"
                sx={{
                  color: shop.color,
                  "&.Mui-checked": {
                    color: shop.color,
                  },
                }}
              />
            }
            label={
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <Box
                  sx={{
                    width: 10,
                    height: 10,
                    borderRadius: "50%",
                    bgcolor: shop.color,
                    flexShrink: 0,
                  }}
                />
                <Typography variant="body2">{shop.name}</Typography>
              </Box>
            }
          />
        ))}
      </FormGroup>

      <Divider sx={{ my: 2 }} />

      {/* Coverage Filters */}
      <Typography variant="subtitle2" fontWeight={600} gutterBottom>
        Coverage Status
      </Typography>
      <FormGroup>
        <FormControlLabel
          control={
            <Checkbox
              checked={showOnlyUnderstaffed}
              onChange={(e) => setShowOnlyUnderstaffed(e.target.checked)}
              size="small"
            />
          }
          label={
            <Typography variant="body2">Show Only Understaffed</Typography>
          }
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={showOnlyOverstaffed}
              onChange={(e) => setShowOnlyOverstaffed(e.target.checked)}
              size="small"
            />
          }
          label={<Typography variant="body2">Show Only Overstaffed</Typography>}
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={showEmptySlots}
              onChange={(e) => setShowEmptySlots(e.target.checked)}
              size="small"
            />
          }
          label={<Typography variant="body2">Show Empty Slots</Typography>}
        />
      </FormGroup>
    </Paper>
  );

  if (timeSlots.length === 0) {
    return (
      <>
        {/* Filter Sidebar */}
        {filterPanelOpen ? renderFilterPanel() : renderMinimizedPanel()}

        <Box sx={{ ml: filterPanelOpen ? "280px" : "64px", height: "100%" }}>
          <Paper sx={{ p: 4, textAlign: "center" }}>
            <Typography variant="body1" color="text.secondary">
              {selectedShops.length === 0
                ? "No shops selected"
                : "No coverage data available"}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {selectedShops.length === 0
                ? "Select shops from the filter panel to view coverage"
                : "Add working hours to employees or adjust filters"}
            </Typography>
          </Paper>
        </Box>
      </>
    );
  }

  return (
    <>
      {/* Filter Sidebar - Expanded or Minimized */}
      {filterPanelOpen ? renderFilterPanel() : renderMinimizedPanel()}

      {/* Main content with margin for fixed sidebar */}
      <Box
        sx={{
          ml: filterPanelOpen ? "280px" : "64px",
          display: "flex",
          flexDirection: "column",
          height: "100%",
          flexGrow: 1,
        }}
      >
        {/* Week Navigator - Sticky */}
        <Box
          sx={{
            position: "sticky",
            top: 0,
            zIndex: 100,
            bgcolor: "background.default",
            pb: 1,
          }}
        >
          <DateNavigator
            mode="weekly"
            selectedDate={formatDateStr(weekStart)}
            onDateChange={onDateChange}
            marginBottom={false}
          />
        </Box>

        {/* Table */}
        <TableContainer
          component={Paper}
          sx={{
            flexGrow: 1,
            overflow: "auto",
            maxHeight: "calc(100vh - 180px)",
          }}
        >
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell
                  sx={{
                    fontWeight: 700,
                    width: 120,
                    position: "sticky",
                    left: 0,
                    bgcolor: "background.paper",
                    zIndex: 5,
                  }}
                >
                  Time
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: 700,
                    width: 150,
                    position: "sticky",
                    left: 120,
                    bgcolor: "background.paper",
                    zIndex: 5,
                  }}
                >
                  Shop
                </TableCell>
                {weekDates.map((date, idx) => {
                  const isToday =
                    date.getDate() === new Date().getDate() &&
                    date.getMonth() === new Date().getMonth() &&
                    date.getFullYear() === new Date().getFullYear();

                  return (
                    <TableCell
                      key={idx}
                      align="center"
                      sx={{
                        fontWeight: 700,
                        bgcolor: "background.paper",
                        borderLeftColor: "primary.main",
                        borderRightColor: "primary.main",
                        borderColor: "gray.400",
                        zIndex: 4,
                      }}
                    >
                      <Typography
                        variant="body2"
                        fontWeight={700}
                        color={isToday ? "primary.main" : "text.primary"}
                      >
                        {date.toLocaleDateString("en-US", { weekday: "short" })}
                      </Typography>
                      <Typography
                        variant="h6"
                        fontWeight={600}
                        color={isToday ? "primary.main" : "text.primary"}
                      >
                        {date.getDate()}
                      </Typography>
                    </TableCell>
                  );
                })}
              </TableRow>
            </TableHead>
            <TableBody>
              {timeSlots.map((time) => {
                // Calculate end time (30 minutes after start)
                const [hours, minutes] = time.split(":").map(Number);
                const startMinutes = hours * 60 + minutes;
                const endMinutes = startMinutes + 30;
                const endHours = Math.floor(endMinutes / 60) % 24;
                const endMins = endMinutes % 60;
                const endTime = `${String(endHours).padStart(2, "0")}:${String(
                  endMins
                ).padStart(2, "0")}`;

                return selectedShops.map((shop, shopIdx) => {
                  const isLastShop = shopIdx === selectedShops.length - 1;

                  return (
                    <TableRow
                      key={`${time}-${shop.id}`}
                      hover
                      sx={{
                        "& td:not(:first-of-type)": {
                          borderBottom: isLastShop ? 1 : "none",
                          borderColor: "grey.300",
                          py: 1,
                        },
                      }}
                    >
                      {/* Time cell - only show for first shop in this time slot */}
                      {shopIdx === 0 && (
                        <TableCell
                          rowSpan={selectedShops.length}
                          sx={{
                            fontWeight: 600,
                            position: "sticky",
                            left: 0,
                            bgcolor: "background.paper",
                            zIndex: 1,
                            borderRight: 1,
                            borderBottom: 1,
                            borderColor: "grey.300",
                            py: 1,
                          }}
                        >
                          {time} - {endTime}
                        </TableCell>
                      )}

                      {/* Shop name cell */}
                      <TableCell
                        sx={{
                          position: "sticky",
                          left: 120,
                          bgcolor: "background.paper",
                          zIndex: 1,
                          borderRight: 1,
                          borderBottom: isLastShop ? 1 : "none",
                          borderColor: "grey.300",
                          py: 1,
                        }}
                      >
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          <Box
                            sx={{
                              width: 12,
                              height: 12,
                              borderRadius: "50%",
                              bgcolor: shop.color,
                              flexShrink: 0,
                            }}
                          />
                          <Typography variant="body2" fontWeight={500}>
                            {shop.name}
                          </Typography>
                        </Box>
                      </TableCell>

                      {/* Date cells */}
                      {weekDates.map((date, dateIdx) => {
                        const dateKey = formatDate(date);
                        const schedule = getScheduleForDate(dateKey, shop.id);
                        const coverageSlots = calculateCoverageSlots(
                          shop,
                          date,
                          schedule
                        );
                        const slot = coverageSlots.find((s) => s.time === time);

                        // Determine status label and styling
                        let label: string = "";
                        let labelColor: string = "";
                        let tooltipText: string = "";
                        let showChip = true;

                        if (!slot) {
                          showChip = false;
                        } else if (!shouldShowChip(slot)) {
                          showChip = false;
                        } else if (slot.required === 0) {
                          label = "Closed";
                          labelColor = "text.secondary";
                          tooltipText = "No coverage required";
                        } else if (slot.allocated >= slot.required) {
                          label = "Filled";
                          labelColor = "success.main";
                          tooltipText = `Assigned: ${slot.allocated} / Needed: ${slot.required}`;
                        } else if (slot.allocated === 0) {
                          label = "Unassigned";
                          labelColor = "error.main";
                          tooltipText = `Assigned: 0 / Needed: ${slot.required}`;
                        } else {
                          label = "Partial";
                          labelColor = "warning.main";
                          tooltipText = `Assigned: ${slot.allocated} / Needed: ${slot.required}`;
                        }

                        return (
                          <TableCell
                            key={dateIdx}
                            align="center"
                            sx={{
                              borderBottom: isLastShop ? 1 : "none",
                              borderColor: "grey.300",
                              bgcolor: "background.paper",
                              py: 1,
                            }}
                          >
                            {showChip && slot ? (
                              <Tooltip title={tooltipText} arrow>
                                <Chip
                                  label={label}
                                  size="small"
                                  onClick={() =>
                                    handleChipClick(
                                      slot,
                                      shop,
                                      date,
                                      time,
                                      schedule
                                    )
                                  }
                                  sx={{
                                    fontSize: "0.7rem",
                                    fontWeight: 600,
                                    height: 22,
                                    minWidth: 70,
                                    cursor: "pointer",
                                    bgcolor: "grey.50",
                                    border: 1,
                                    borderColor: "grey.300",
                                    borderLeft: `3px solid ${shop.color}`,
                                    borderRadius: 1,
                                    transition: "all 0.2s",
                                    "&:hover": {
                                      transform: "scale(1.05)",
                                      boxShadow: 2,
                                      bgcolor: "grey.100",
                                    },
                                    "& .MuiChip-label": {
                                      color: labelColor,
                                    },
                                  }}
                                />
                              </Tooltip>
                            ) : (
                              <Typography
                                variant="caption"
                                color="text.disabled"
                              >
                                -
                              </Typography>
                            )}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  );
                });
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      {/* Employee Allocation Dialog */}
      <Dialog
        open={allocationDialogOpen}
        onClose={() => setAllocationDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
          },
        }}
      >
        {selectedSlotInfo &&
          (() => {
            const availableEmployees = getAvailableEmployees(
              selectedSlotInfo.shop,
              selectedSlotInfo.date,
              selectedSlotInfo.timeSlot
            );
            const endTime = calculateEndTime(selectedSlotInfo.timeSlot, 0.5);

            return (
              <>
                <DialogTitle
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    bgcolor: selectedSlotInfo.shop.color,
                    color: "#ffffff",
                  }}
                >
                  <InfoOutlinedIcon />
                  <Box>
                    <Typography variant="h6" fontWeight={600}>
                      Allocate Employee
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                      {selectedSlotInfo.shop.name} • {selectedSlotInfo.timeSlot}{" "}
                      - {endTime}
                    </Typography>
                  </Box>
                </DialogTitle>
                <DialogContent sx={{ mt: 2 }}>
                  <Stack spacing={3}>
                    {/* Date and Coverage Info */}
                    <Box
                      sx={{
                        p: 2,
                        borderRadius: 1,
                        bgcolor: "grey.50",
                        border: 1,
                        borderColor: "grey.300",
                      }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        <strong>Date:</strong>{" "}
                        {selectedSlotInfo.date.toLocaleDateString("en-US", {
                          weekday: "long",
                          month: "long",
                          day: "numeric",
                          year: "numeric",
                        })}
                      </Typography>
                      <Box sx={{ display: "flex", gap: 3, mt: 1 }}>
                        <Typography variant="body2">
                          <strong>Required:</strong> {selectedSlotInfo.required}
                        </Typography>
                        <Typography variant="body2">
                          <strong>Assigned:</strong>{" "}
                          {selectedSlotInfo.allocated}
                        </Typography>
                        <Typography
                          variant="body2"
                          color={
                            selectedSlotInfo.allocated >=
                            selectedSlotInfo.required
                              ? "success.main"
                              : "error.main"
                          }
                          fontWeight={600}
                        >
                          <strong>Shortage:</strong>{" "}
                          {Math.max(
                            0,
                            selectedSlotInfo.required -
                              selectedSlotInfo.allocated
                          )}
                        </Typography>
                      </Box>
                    </Box>

                    <Divider />

                    {/* Available Employees List */}
                    <Box>
                      <Typography
                        variant="subtitle2"
                        fontWeight={600}
                        gutterBottom
                      >
                        Available Employees ({availableEmployees.length})
                      </Typography>
                      {availableEmployees.length === 0 ? (
                        <Box
                          sx={{
                            p: 3,
                            textAlign: "center",
                            bgcolor: "grey.50",
                            borderRadius: 1,
                          }}
                        >
                          <Typography variant="body2" color="text.secondary">
                            No employees available for this time slot
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Employees must be assigned to shop, have
                            availability, and no conflicts
                          </Typography>
                        </Box>
                      ) : (
                        <Box
                          sx={{
                            maxHeight: 300,
                            overflow: "auto",
                            border: 1,
                            borderColor: "grey.300",
                            borderRadius: 1,
                          }}
                        >
                          {availableEmployees.map((empInfo) => (
                            <Box
                              key={empInfo.employee.id}
                              onClick={() => {
                                setSelectedEmployee(empInfo.employee);
                                setAllocationHours(
                                  Math.min(empInfo.remainingHours, 8)
                                );
                              }}
                              sx={{
                                p: 2,
                                cursor: "pointer",
                                bgcolor:
                                  selectedEmployee?.id === empInfo.employee.id
                                    ? "primary.lighter"
                                    : "transparent",
                                borderBottom: 1,
                                borderColor: "grey.100",
                                transition: "all 0.2s",
                                "&:hover": {
                                  bgcolor:
                                    selectedEmployee?.id === empInfo.employee.id
                                      ? "primary.lighter"
                                      : "grey.50",
                                },
                                "&:last-child": {
                                  borderBottom: 0,
                                },
                              }}
                            >
                              <Box
                                sx={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  alignItems: "center",
                                }}
                              >
                                <Box>
                                  <Typography variant="body1" fontWeight={600}>
                                    {empInfo.employee.name}
                                  </Typography>
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                  >
                                    Available: {empInfo.availableHours}h •
                                    Scheduled: {empInfo.scheduledHours}h •
                                    Remaining:{" "}
                                    <Box
                                      component="span"
                                      sx={{
                                        color: "success.main",
                                        fontWeight: 600,
                                      }}
                                    >
                                      {empInfo.remainingHours.toFixed(1)}h
                                    </Box>
                                  </Typography>
                                </Box>
                                {selectedEmployee?.id ===
                                  empInfo.employee.id && (
                                  <Chip
                                    label="Selected"
                                    color="primary"
                                    size="small"
                                  />
                                )}
                              </Box>
                            </Box>
                          ))}
                        </Box>
                      )}
                    </Box>

                    {/* Allocation Hours */}
                    {selectedEmployee && (
                      <>
                        <Divider />
                        <Box>
                          <Typography
                            variant="subtitle2"
                            fontWeight={600}
                            gutterBottom
                          >
                            Allocation Details
                          </Typography>
                          <Box sx={{ mt: 2 }}>
                            <TimePicker24
                              label="Start Time"
                              value={selectedSlotInfo.timeSlot}
                              onChange={() => {}}
                              disabled
                              fullWidth
                            />
                          </Box>
                          <Box sx={{ mt: 2 }}>
                            {(() => {
                              const maxHours = Math.min(
                                availableEmployees.find(
                                  (e) => e.employee.id === selectedEmployee.id
                                )?.remainingHours || 8,
                                12
                              );
                              const safeMax = Math.max(maxHours, 0.5);
                              return (
                                <>
                                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
                                    <Typography variant="body2" color="text.secondary">
                                      Duration
                                    </Typography>
                                    <Typography variant="body1" fontWeight={600} color="primary">
                                      {allocationHours}h
                                      <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                                        (ends {calculateEndTime(selectedSlotInfo.timeSlot, allocationHours)})
                                      </Typography>
                                    </Typography>
                                  </Box>
                                  <Slider
                                    value={allocationHours}
                                    onChange={(_, value) => setAllocationHours(value as number)}
                                    min={0.5}
                                    max={safeMax}
                                    step={0.5}
                                    marks={[
                                      { value: 0.5, label: "0.5h" },
                                      { value: safeMax / 2, label: `${(safeMax / 2).toFixed(1)}h` },
                                      { value: safeMax, label: `${safeMax.toFixed(1)}h` },
                                    ]}
                                    valueLabelDisplay="auto"
                                    valueLabelFormat={(value) => `${value}h`}
                                    sx={{
                                      "& .MuiSlider-markLabel": {
                                        fontSize: "0.75rem",
                                      },
                                    }}
                                  />
                                </>
                              );
                            })()}
                          </Box>
                        </Box>
                      </>
                    )}
                  </Stack>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                  <Button
                    onClick={() => setAllocationDialogOpen(false)}
                    color="inherit"
                    disabled={isAllocating}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleAllocateEmployee}
                    variant="contained"
                    disabled={
                      !selectedEmployee || allocationHours <= 0 || isAllocating
                    }
                    sx={{ minWidth: 120 }}
                  >
                    {isAllocating ? "Allocating..." : "Allocate"}
                  </Button>
                </DialogActions>
              </>
            );
          })()}
      </Dialog>
    </>
  );
}
